<script setup>
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import TableBody from "@/components/global-themed/table/TableBody.vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import {useInfiniteScroll} from "@vueuse/core";
import {computed, ref} from "vue";
import TemplateRow from "@/components/email-templates/partials/table/TemplateRow.vue";
import SearchInput from "@/components/global-themed/SearchInput.vue";
import {useTemplateFilters, useTemplates} from "@/composables/email-templates/useEmailTemplates";
import EmailTemplateTableHeader from "@/components/email-templates/partials/table/EmailTemplateTableHeader.vue";

defineProps({
  adminView: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const tableBody = ref(null);
const {search} = useTemplateFilters();

const {
  query,
  templates,
  visibleRows,
} = useTemplates();

const { isLoading } = query;

useInfiniteScroll(
  computed(() => tableBody.value?.$el),
  () => {
    visibleRows.value += 50;
  },
  {
    distance: 10,
  }
);
</script>

<template>
  <div class="tw-m-auto tw-max-w-default-container">
    <div class="tw-flex tw-flex-col tw-gap-y-8">
      <div>
        <h1 class="tw-font-bold tw-text-4.5xl tw-text-neutral-900">
          {{ adminView ? 'Admin ' : '' }}Email Template Manager
        </h1>

        <section v-if="adminView">
          <p class="tw-text-neutral tw-text-base">
            This is the Admin Email Template Manager. Here you can edit emails sent by the platform to white-label users. These templates will be used when the organisation has not created their own template content.
          </p>
          <p class="tw-text-danger tw-text-base">
            <strong>Important:</strong> These emails must remain fully white-labeled and must not reference Kit, HIKE SEO, or any related branding.
          </p>
        </section>

        <p
          v-else
          class="tw-text-neutral tw-text-base"
        >
          Welcome to the Email Template Manager. Here you can edit the content for all of the email templates the platform sends. Once you’ve edited the email templates, head over to the user permissions to decide what emails each users will receive.
        </p>
      </div>

      <div
        v-if="isLoading"
        class="tw-bg-white tw-p-8 tw-rounded-lg"
      >
        <LoadingBlock />
      </div>

      <div
        v-show="!isLoading"
        class="tw-bg-white tw-p-8 tw-rounded-lg tw-flex tw-flex-col tw-gap-y-8"
      >
        <div class="tw-grid tw-grid-cols-12">
          <div class="tw-col-span-4 tw-col-start-9">
            <SearchInput
              v-model="search"
              placeholder="Search"
            />
          </div>
        </div>

        <DataTable>
          <EmailTemplateTableHeader />
          <TableBody
            v-if="templates?.length > 0"
            ref="tableBody"
            class="tw-max-h-[70vh]"
          >
            <TemplateRow
              v-for="template in templates"
              :key="template.id"
              :template="template"
              :admin-view="adminView"
            />
          </TableBody>

          <ZeroContent v-if="!templates || !templates.length">
            Looks like there are no email templates to display.
          </ZeroContent>
        </DataTable>
      </div>
    </div>
  </div>
</template>

